import { useUnleashClient } from '@unleash/proxy-client-react'
import { useEffect, useState, type PropsWithChildren } from 'react'
import { useUserStore } from '~/entities/user'
import { NotFoundPage } from '~/pages/not-found'
import {
  useAreFeatureFlagsReady,
  useIsFeatureEnabled,
  type FeatureFlagKey,
} from '~/shared/lib/feature-flags'

type FlagAccess = { isLoading: true; isAllowed: false } | { isLoading: false; isAllowed: boolean }

/** A hung Unleash request must not leave the page blank; past this, "off" is trusted as is. */
const FLAG_REFETCH_TIMEOUT_MS = 5000

/**
 * Access on a flag targeted at the user's email. Flags fetched before Okta's userInfo landed
 * were evaluated without the email, so "off" is only trusted once a fetch made with the email
 * has finished.
 */
function useUserFlagAccess(flag: FeatureFlagKey): FlagAccess {
  const isEnabled = useIsFeatureEnabled(flag)
  const areFlagsReady = useAreFeatureFlagsReady()
  const email = useUserStore((state) => state.userInfo?.email)
  const userEmail = typeof email === 'string' ? email : undefined
  const unleashClient = useUnleashClient()
  const [refetchedFor, setRefetchedFor] = useState<string>()
  const needsRefetch = areFlagsReady && !!userEmail && !isEnabled && refetchedFor !== userEmail

  useEffect(() => {
    if (!needsRefetch || !userEmail) return

    let isCurrent = true
    const markRefetched = () => {
      if (isCurrent) setRefetchedFor(userEmail)
    }
    // Deferred past SecureRoute's own context update in this commit: the SDK aborts an
    // in-flight fetch when a newer one starts, so only the last caller's promise means
    // the toggles were fetched with the email.
    const refetchTimer = setTimeout(() => {
      unleashClient.setContextField('userId', userEmail).then(markRefetched, markRefetched)
    }, 0)
    const giveUpTimer = setTimeout(markRefetched, FLAG_REFETCH_TIMEOUT_MS)

    return () => {
      isCurrent = false
      clearTimeout(refetchTimer)
      clearTimeout(giveUpTimer)
    }
  }, [needsRefetch, userEmail, unleashClient])

  if (!areFlagsReady || !userEmail) {
    return { isLoading: true, isAllowed: false }
  }
  if (isEnabled) {
    return { isLoading: false, isAllowed: true }
  }
  if (refetchedFor !== userEmail) {
    return { isLoading: true, isAllowed: false }
  }
  return { isLoading: false, isAllowed: false }
}

/** Renders nothing while access resolves, and the 404 page when the flag is off. */
export function FlagGate({ flag, children }: PropsWithChildren<{ flag: FeatureFlagKey }>) {
  const { isLoading, isAllowed } = useUserFlagAccess(flag)

  if (isLoading) {
    return null
  }

  return isAllowed ? children : <NotFoundPage />
}
